/**
 * @prettier
 */

// React Packages
import { useRef, useState, useEffect } from "react";
import { connect } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";
import { bindActionCreators } from "redux";
import i18next from "i18next";

// Services
import {
    initialize,
    findImage,
    initalizeNativeCapture,
} from "../../services/shared/regulaService";
import { setCapturedDocumentImage } from "../../state/actions/idPropertiesActions";
// Services
import DataDogService from "../../services/shared/datadogService";

// Config
import * as regulaConfig from "../../config/regula";

// Components
import Header from "../../components/Header";
import Navigator from "../../components/Navigator";
import CustomButton from "../../components/CustomButton";
import SubmissionFlowStepper from "../../components/SubmissionFlowStepper";
import { LoadingStateAnimation } from "../../components/ProcessingLayout";

const RegulaCapturePage = ({ setCapturedDocumentImage, location }) => {
    const [navigation, setNavigation] = useState({
        action: "load",
        props: null,
    });

    const [fileReceived, setFileReceived] = useState(false);
    const [useFallback, setUseFallback] = useState(false);
    const [showFrame, setShowFrame] = useState(false);
    const [renderSDK, setRenderSDK] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);

    const captureHandlerRef = useRef(null);
    const { t } = useTranslation();

    const listener = event => {
        switch (event.detail.action) {
            case "PRESS_RETRY_BUTTON":
                DataDogService.log("Retrying Regula Capture");
                setShowFrame(false);
                break;

            case "PROCESS_FINISHED":
                setIsProcessing(false);
                if (event.detail.data.status === 1) {
                    DataDogService.log("Image Captured with Regula SDK");
                    processImage(event.detail.data.response);
                } else if (event.detail.data.status === 0) {
                    DataDogService.log("Non-success from Regula SDK");
                    DataDogService.log(
                        "Reason for failure: " + event.detail.data.reason
                    );
                    if (!useFallback) {
                        DataDogService.log("Fallback to Native Camera");
                        setUseFallback(true);
                        setShowFrame(true);
                        setupNativeCapture();
                    }
                } else {
                    DataDogService.log("Regula Capture Timeout");
                    setShowFrame(true);
                }
                break;

            default:
                break;
        }
    };

    useEffect(() => {
        initialize();
        const documentReaderComponent =
            document.querySelector("document-reader");

        documentReaderComponent.settings = regulaConfig.documentReaderSettings;

        const documentReaderHandler = event => listener(event);
        documentReaderComponent.addEventListener(
            "document-reader",
            documentReaderHandler
        );

        return () => {
            // Cleanup
            window.RegulaDocumentSDK.shutdown();
            documentReaderComponent.removeEventListener(
                "document-reader",
                documentReaderHandler
            );
        };
    }, []);

    useEffect(() => {
        if (fileReceived) {
            setIsProcessing(true);
        }
    }, [fileReceived]);

    const processImage = response => {
        if (window.RegulaProcessor) {
            // Clean up processor when we are done
            window.RegulaProcessor.shutdown();
        }
        DataDogService.log("Processing Image");
        setCapturedDocumentImage(findImage(response, true), "cropped");
        setCapturedDocumentImage(findImage(response, false), "uncropped");

        setNavigation({
            action: "next",
            props: { documentId: location.state.documentId },
        });
    };

    const openCamera = () => {
        document.getElementById("capture").click();
        setRenderSDK(false);
    };

    const setupNativeCapture = () => {
        // Shut down regula sdk before starting native capture
        window.RegulaDocumentSDK.shutdown();
        const captureInput = document.getElementById("capture");

        const captureHandler = function () {
            if (this.files[0]) {
                setFileReceived(true);
                DataDogService.log("Image uploaded with Native Camera");
                initalizeNativeCapture(this.files, processImage);
            } else {
                DataDogService.createError(
                    "No image uploaded to Native Camera"
                );
            }
        };

        captureHandlerRef.current = captureHandler;

        captureInput.addEventListener("change", captureHandler, { once: true });
    };

    useEffect(() => {
        return () => {
            const captureInput = document.getElementById("capture");

            if (captureInput && captureHandlerRef.current) {
                captureInput.removeEventListener(
                    "change",
                    captureHandlerRef.current
                );
            }
        };
    }, []);

    return (
        <>
            <div className="root-layout-container">
                {showFrame && (
                    <>
                        <Header></Header>
                        <SubmissionFlowStepper></SubmissionFlowStepper>
                    </>
                )}
                {isProcessing && fileReceived && <LoadingStateAnimation />}
            </div>

            <document-reader
                capture-button="true"
                new-layout="true"
                locale={i18next.languages[0]}
                class={renderSDK ? (showFrame ? "contained" : "") : "invisible"}
            ></document-reader>
            {useFallback && (
                <div className="root-layout-container">
                    {!fileReceived && (
                        <CustomButton
                            isFocused
                            label={t("idpal_open_camera")}
                            handleClick={openCamera}
                        />
                    )}
                    <input
                        className="hidden"
                        type="file"
                        id="capture"
                        accept="image/*"
                        capture
                    />
                </div>
            )}

            <Navigator
                page={"document_capture"}
                action={navigation.action}
                propsToPass={navigation.props}
            />
        </>
    );
};

function mapStateToProps(state) {}

function mapDispatchToProps(dispatch) {
    const actions = bindActionCreators({ setCapturedDocumentImage }, dispatch);
    return { ...actions, dispatch };
}

export default withTranslation("translation")(
    connect(mapStateToProps, mapDispatchToProps)(RegulaCapturePage)
);
